// ========================================
// HOME MANAGER
// Gestionnaire de la page d'accueil
// ========================================

import React, { useState } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import useHomePage from '../../../hooks/useHomePage';
import useProjects from '../../../hooks/useProjects';
import { ImageUploader, RichTextEditor } from '../../../components/admin/Editor';
import { 
  FiSave, 
  FiImage, 
  FiType, 
  FiGrid, 
  FiLayers,
  FiStar,
  FiPlus,
  FiTrash2,
  FiMove,
  FiEdit2,
  FiCheck,
  FiX,
  FiLoader
} from 'react-icons/fi';
import './HomeManager.css';

// ============================================
// LISTE DES ICÔNES DISPONIBLES
// ============================================
const AVAILABLE_ICONS = [
  { value: 'cv', label: '📄 Document / CV' },
  { value: 'users', label: '👥 Utilisateurs / Équipe' },
  { value: 'pen', label: '✍️ Écriture / Rédaction' },
  { value: 'share', label: '🔗 Partage / Réseau' },
  { value: 'mic', label: '🎤 Microphone / Audio' },
  { value: 'camera', label: '📷 Caméra / Photo' },
  { value: 'video', label: '🎬 Vidéo' },
  { value: 'megaphone', label: '📢 Mégaphone / Communication' },
  { value: 'book', label: '📚 Livre / Formation' },
  { value: 'star', label: '⭐ Étoile / Qualité' },
  { value: 'heart', label: '❤️ Cœur / Passion' },
  { value: 'target', label: '🎯 Cible / Objectif' },
  { value: 'award', label: '🏆 Récompense / Prix' },
  { value: 'globe', label: '🌍 Globe / International' },
  { value: 'message', label: '💬 Message / Chat' },
  { value: 'mail', label: '✉️ Email / Contact' },
  { value: 'lightbulb', label: '💡 Idée / Innovation' },
  { value: 'rocket', label: '🚀 Fusée / Lancement' },
  { value: 'chart', label: '📊 Graphique / Statistiques' },
  { value: 'code', label: '💻 Code / Développement' },
  { value: 'palette', label: '🎨 Palette / Design' },
  { value: 'music', label: '🎵 Musique / Audio' },
  { value: 'calendar', label: '📅 Calendrier / Événement' },
  { value: 'clock', label: '⏰ Horloge / Temps' },
  { value: 'shield', label: '🛡️ Bouclier / Sécurité' },
  { value: 'zap', label: '⚡ Éclair / Rapidité' },
  { value: 'trending', label: '📈 Tendance / Croissance' },
  { value: 'handshake', label: '🤝 Partenariat' },
  { value: 'search', label: '🔍 Recherche / Analyse' },
  { value: 'settings', label: '⚙️ Paramètres / Technique' }
];

const HomeManager = () => {
  const { content, loading, saving, saveHero, saveIntroduction, saveSypnase, saveServices } = useHomePage();
  const { projects } = useProjects();
  const [activeTab, setActiveTab] = useState('hero');

  // États locaux pour l'édition
  const [heroData, setHeroData] = useState(null);
  const [introData, setIntroData] = useState(null);
  const [sypnaseData, setSypnaseData] = useState(null);
  const [servicesData, setServicesData] = useState(null);
  const [editingService, setEditingService] = useState(null);

  // Initialiser les données quand le contenu est chargé
  React.useEffect(() => {
    if (content) {
      setHeroData(content.hero);
      setIntroData(content.introduction);
      setSypnaseData(content.sypnase);
      setServicesData(content.services || []);
    }
  }, [content]);

  const tabs = [
    { id: 'hero', label: 'Hero', icon: FiImage },
    { id: 'intro', label: 'Introduction', icon: FiType },
    { id: 'sypnase', label: 'Sypnase', icon: FiLayers },
    { id: 'services', label: 'Services', icon: FiGrid },
    { id: 'featured', label: 'Projets vedettes', icon: FiStar },
  ];

  const handleSaveHero = async () => {
    await saveHero(heroData);
  };

  const handleSaveIntro = async () => {
    await saveIntroduction(introData);
  };

  const handleSaveSypnase = async () => {
    await saveSypnase(sypnaseData);
  };

  const handleSaveServices = async () => {
    await saveServices(servicesData);
  };

  const addNewService = () => {
    const newService = {
      id: Date.now().toString(),
      icon: 'star',
      title: 'Nouveau service',
      description: 'Description du service',
      order: servicesData.length
    };
    setServicesData([...servicesData, newService]);
    setEditingService(newService.id);
  };

  const updateService = (id, field, value) => {
    setServicesData(prev =>
      prev.map(s => s.id === id ? { ...s, [field]: value } : s)
    );
  };

  const deleteService = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      setServicesData(prev => prev.filter(s => s.id !== id));
    }
  };

  const moveService = (index, direction) => {
    const newServices = [...servicesData];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newServices.length) return;
    
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];
    setServicesData(newServices);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="manager-loading">
          <FiLoader className="spinner" />
          <p>Chargement du contenu...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="home-manager">
        {/* Header */}
        <div className="manager-header">
          <div>
            <h1>Gestion de l'Accueil</h1>
            <p>Personnalisez le contenu de votre page d'accueil</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="manager-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`manager-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="manager-content">
          {/* Hero Section */}
          {activeTab === 'hero' && heroData && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Section Hero</h2>
                <p>L'en-tête principal de votre site</p>
              </div>

              <div className="form-card">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label className="form-label">Photo de profil</label>
                    <ImageUploader
                      value={heroData.profileImage}
                      onChange={(url) => setHeroData({ ...heroData, profileImage: url })}
                      folder="portfolio/profile"
                      placeholder="Uploadez votre photo de profil"
                    />
                  </div>
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label className="form-label">Titre principal</label>
                    <input
                      type="text"
                      className="form-input"
                      value={heroData.title || ''}
                      onChange={(e) => setHeroData({ ...heroData, title: e.target.value })}
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Sous-titre</label>
                    <input
                      type="text"
                      className="form-input"
                      value={heroData.subtitle || ''}
                      onChange={(e) => setHeroData({ ...heroData, subtitle: e.target.value })}
                      placeholder="Votre fonction"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Slogan</label>
                  <textarea
                    className="form-input form-textarea"
                    value={heroData.slogan || ''}
                    onChange={(e) => setHeroData({ ...heroData, slogan: e.target.value })}
                    placeholder="Votre slogan accrocheur"
                    rows={3}
                  />
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label className="form-label">Texte du bouton</label>
                    <input
                      type="text"
                      className="form-input"
                      value={heroData.ctaText || ''}
                      onChange={(e) => setHeroData({ ...heroData, ctaText: e.target.value })}
                      placeholder="Découvrir mes projets"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Lien du bouton</label>
                    <input
                      type="text"
                      className="form-input"
                      value={heroData.ctaLink || ''}
                      onChange={(e) => setHeroData({ ...heroData, ctaLink: e.target.value })}
                      placeholder="/projets"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveHero}
                    disabled={saving}
                  >
                    {saving ? <FiLoader className="spinner" /> : <FiSave />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Introduction Section */}
          {activeTab === 'intro' && introData && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Section Introduction</h2>
                <p>Présentez-vous en quelques mots</p>
              </div>

              <div className="form-card">
                <div className="form-group">
                  <label className="form-label">Texte de présentation</label>
                  <RichTextEditor
                    value={introData.content || ''}
                    onChange={(content) => setIntroData({ ...introData, content })}
                    placeholder="Écrivez votre texte de présentation..."
                    minHeight={250}
                  />
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveIntro}
                    disabled={saving}
                  >
                    {saving ? <FiLoader className="spinner" /> : <FiSave />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sypnase Section */}
          {activeTab === 'sypnase' && sypnaseData && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Section Sypnase</h2>
                <p>Présentez votre agence</p>
              </div>

              <div className="form-card">
                <div className="form-group">
                  <label className="form-label">Logo de l'agence</label>
                  <ImageUploader
                    value={sypnaseData.logo}
                    onChange={(url) => setSypnaseData({ ...sypnaseData, logo: url })}
                    folder="portfolio/sypnase"
                    placeholder="Logo de Sypnase"
                  />
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label className="form-label">Nom de l'agence</label>
                    <input
                      type="text"
                      className="form-input"
                      value={sypnaseData.title || ''}
                      onChange={(e) => setSypnaseData({ ...sypnaseData, title: e.target.value })}
                      placeholder="Sypnase"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Slogan</label>
                    <input
                      type="text"
                      className="form-input"
                      value={sypnaseData.slogan || ''}
                      onChange={(e) => setSypnaseData({ ...sypnaseData, slogan: e.target.value })}
                      placeholder="Human + Tech Link"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-input form-textarea"
                    value={sypnaseData.description || ''}
                    onChange={(e) => setSypnaseData({ ...sypnaseData, description: e.target.value })}
                    placeholder="Description de l'agence..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Lien vers le site</label>
                  <input
                    type="url"
                    className="form-input"
                    value={sypnaseData.link || ''}
                    onChange={(e) => setSypnaseData({ ...sypnaseData, link: e.target.value })}
                    placeholder="https://sypnase.com"
                  />
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveSypnase}
                    disabled={saving}
                  >
                    {saving ? <FiLoader className="spinner" /> : <FiSave />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Services Section */}
          {activeTab === 'services' && servicesData && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Section Services</h2>
                <p>Les services que vous proposez</p>
              </div>

              <div className="services-list">
                {servicesData.map((service, index) => (
                  <div key={service.id} className="service-card">
                    {editingService === service.id ? (
                      <div className="service-edit-form">
                        <div className="form-row two-cols">
                          <div className="form-group">
                            <label className="form-label">Icône</label>
                            <select
                              className="form-input"
                              value={service.icon}
                              onChange={(e) => updateService(service.id, 'icon', e.target.value)}
                            >
                              {AVAILABLE_ICONS.map(icon => (
                                <option key={icon.value} value={icon.value}>
                                  {icon.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Titre</label>
                            <input
                              type="text"
                              className="form-input"
                              value={service.title}
                              onChange={(e) => updateService(service.id, 'title', e.target.value)}
                              placeholder="Nom du service"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-input form-textarea"
                            value={service.description}
                            onChange={(e) => updateService(service.id, 'description', e.target.value)}
                            placeholder="Décrivez ce service..."
                            rows={3}
                          />
                        </div>

                        <div className="service-edit-actions">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setEditingService(null)}
                          >
                            <FiCheck />
                            Terminé
                          </button>
                          <button
                            className="btn btn-sm btn-ghost"
                            onClick={() => setEditingService(null)}
                          >
                            <FiX />
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="service-preview">
                        <div className="service-order-controls">
                          <button
                            className="order-btn"
                            onClick={() => moveService(index, -1)}
                            disabled={index === 0}
                            title="Monter"
                          >
                            ↑
                          </button>
                          <button
                            className="order-btn"
                            onClick={() => moveService(index, 1)}
                            disabled={index === servicesData.length - 1}
                            title="Descendre"
                          >
                            ↓
                          </button>
                        </div>
                        <div className="service-icon-preview">
                          {service.icon || 'star'}
                        </div>
                        <div className="service-info">
                          <h4>{service.title}</h4>
                          <p>{service.description}</p>
                        </div>
                        <div className="service-actions">
                          <button
                            className="action-btn"
                            onClick={() => setEditingService(service.id)}
                            title="Modifier"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={() => deleteService(service.id)}
                            title="Supprimer"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <button className="add-service-btn" onClick={addNewService}>
                  <FiPlus />
                  Ajouter un service
                </button>
              </div>

              <div className="form-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveServices}
                  disabled={saving}
                >
                  {saving ? <FiLoader className="spinner" /> : <FiSave />}
                  Sauvegarder les services
                </button>
              </div>
            </div>
          )}

          {/* Featured Projects */}
          {activeTab === 'featured' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Projets en vedette</h2>
                <p>Sélectionnez les projets à mettre en avant sur l'accueil</p>
              </div>

              <div className="form-card">
                {projects.length > 0 ? (
                  <div className="projects-selector">
                    {projects.map(project => (
                      <label key={project.id} className="project-checkbox">
                        <input
                          type="checkbox"
                          checked={content?.featuredProjects?.includes(project.id) || false}
                          onChange={(e) => {
                            // TODO: Implémenter la sélection
                          }}
                        />
                        <div className="project-checkbox-content">
                          {project.mainImage && (
                            <img src={project.mainImage} alt={project.title} />
                          )}
                          <span>{project.title}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <FiStar />
                    <p>Aucun projet disponible</p>
                    <span>Créez d'abord des projets dans le gestionnaire de projets</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default HomeManager;